/**
 * Matching a live `project.update` against the acknowledgment it is waiting for
 * (BRAI-689).
 *
 * The socket carries one of these at a time but the reader can start a second
 * before the first lands — the server drops an update still loading when a new
 * one arrives, so only the last one is ever acknowledged. Counting
 * acknowledgments would therefore settle the wrong request: the first reply to
 * arrive would clear a pending state belonging to a change that was abandoned.
 *
 * Matched on the values instead. The server echoes what it *applied* after
 * merging the keys the request left out, so an echo that agrees with everything
 * this request asked for is this request's answer whatever else was in flight.
 */

/** What a caller asks the server to change. An absent key means "leave it". */
export interface ProjectUpdateRequest {
  /** Backlog project key, or null to take the project off the meeting. */
  projectKey?: string | null
  /** The reader's numeric Backlog account id. */
  userIdBacklog?: number
}

/** The applied values, as `project.updated` reports them. */
export interface ProjectUpdatedEcho {
  projectKey?: string | null
  userIdBacklog?: number | null
}

/**
 * Whether a request names at least one thing to change.
 *
 * The server answers `bad_request` to a frame carrying neither key, so this
 * stops the trip before it costs a round of the reader's attention.
 */
export function isSendableProjectUpdate(request: ProjectUpdateRequest): boolean {
  return 'projectKey' in request || typeof request.userIdBacklog === 'number'
}

/**
 * Whether `echo` is the acknowledgment for `request`.
 *
 * Every key the request named has to agree. A key it left out is not compared:
 * the echo reports the applied value for it, which is whatever was already
 * there and says nothing about whether this request was the one applied.
 *
 * @param request What was sent.
 * @param echo The `project.updated` payload that arrived.
 * @returns True when this acknowledgment settles this request.
 */
export function acknowledgesProjectUpdate(
  request: ProjectUpdateRequest,
  echo: ProjectUpdatedEcho
): boolean {
  if (!isSendableProjectUpdate(request)) return false

  if ('projectKey' in request) {
    // Both sides normalised to null, because "no project" reaches this
    // comparison as an absent key from the server and an explicit null from
    // the caller, and those are the same answer.
    const asked = request.projectKey ?? null
    const applied = echo.projectKey ?? null
    if (asked !== applied) return false
  }

  if (typeof request.userIdBacklog === 'number') {
    if (echo.userIdBacklog !== request.userIdBacklog) return false
  }

  return true
}
